import { isLoading } from "apollo/utils";
import { QueryProgress } from "core/types";
import { useWarnBeforeNavigate } from "dsl/hooks/useWarnBeforeNavigate";
import { useMemo } from "react";

type AHBOperationProgress = {
  fetchXMLProgress: QueryProgress;
  progress: (id: string) => QueryProgress;
  progress2: (id: string) => QueryProgress;
  sendProgress: QueryProgress;
  sendToHisProgress: (id: string) => QueryProgress;
  withdrawProgress: QueryProgress;
};

/**
 * Custom hook to prevent navigation during AHB operations
 * Shows a warning dialog when user tries to navigate away while operations are in progress
 */
export function useAHBNavigationPrevention({
  fetchXMLProgress,
  progress,
  progress2,
  sendProgress,
  sendToHisProgress,
  withdrawProgress,
}: AHBOperationProgress) {
  // Check if any AHB operation is currently in progress
  const isAnyOperationInProgress = useMemo(() => {
    return (
      isLoading(sendProgress) ||
      isLoading(withdrawProgress) ||
      isLoading(fetchXMLProgress) ||
      // Check if any PDF generation is in progress
      Object.values(progress).some(isLoading) ||
      Object.values(progress2).some(isLoading) ||
      // Check if any HIS sending is in progress
      Object.values(sendToHisProgress).some(isLoading)
    );
  }, [
    sendProgress,
    withdrawProgress,
    progress,
    progress2,
    sendToHisProgress,
    fetchXMLProgress,
  ]);

  // Prevent navigation while operations are in progress
  useWarnBeforeNavigate({
    onConfirm: async () => {
      // Allow navigation after confirmation
    },
    skip: !isAnyOperationInProgress,
  });

  return isAnyOperationInProgress;
}
